import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Select, Textarea } from '../components/ui/Input';

/*
 * Trading term agreements (migration 070) — Matthew, 18 Sep:
 *
 *   "In addition, since we will set up an additional button for customer terms, can we
 *    please also create another button for Trading Term agreements where we can upload
 *    documents to be saved by customer, whether direct or indirect, with a start and
 *    finish for each agreement."
 *
 * The signed paper behind the numbers on the Customer Terms tab. Optionally tied to a
 * specific terms set — but only optionally, because a signed agreement usually arrives
 * before anyone has keyed the terms it contains, and a file you cannot save until the
 * data entry is done is a file that ends up in someone's inbox instead.
 *
 * 078 (9 Oct): this is now the CUSTOMER CONTRACT REGISTER — customer contracts live here,
 * purchasing/procurement contracts (incl. suppliers Sales owns, e.g. Liaise) live in
 * app-purchasing's register. Status is the human decision; the lifecycle (notice window,
 * expiring, review due, auto-renew risk, date gaps) is derived by the API on every read.
 */

const MAX_FILE_MB = 15;
const CARD = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 };
const LBL = { color: 'var(--text-muted)', fontSize: 11, marginBottom: 3 };
const TH = { textAlign: 'left', padding: '7px 9px', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' };
const TD = { padding: '7px 9px', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-soft)' };

const CONTRACT_TYPES = [
  { key: 'trading-terms', label: 'Trading terms' },
  { key: 'supply',        label: 'Supply agreement' },
  { key: 'distribution',  label: 'Distribution / export' },
  { key: 'private-label', label: 'Private label' },
  { key: 'promotional',   label: 'Promotional / marketing' },
  { key: 'nda',           label: 'NDA / confidentiality' },
  { key: 'other',         label: 'Other' },
];
const typeLabel = (k) => CONTRACT_TYPES.find(t => t.key === k)?.label ?? k ?? '—';

const MUTED = { color: 'var(--text-muted)', soft: 'var(--border)' };
const WARN = { color: 'var(--warn-text)', soft: 'var(--warn-soft)' };
const DANGER = { color: 'var(--danger)', soft: 'var(--danger-soft)' };

/**
 * derivedState comes from the API (utils/contractLifecycle.ts) — never worked out here, so
 * the page and anything else reading the endpoint cannot disagree. `rank` orders the
 * worklist: what bites soonest, first.
 */
function stateBadge(a) {
  switch (a.derivedState) {
    case 'notice-window': return { label: a.autoRenewRisk ? 'Auto-renews — act now' : `Notice by ${a.noticeByDate}`, ...DANGER, rank: 0 };
    case 'expiring':      return { label: `Ends in ${a.daysToEnd}d`, ...WARN, rank: 1 };
    case 'review-due':    return { label: 'Review due', ...WARN, rank: 2 };
    case 'draft':         return { label: 'Draft — not cover', ...MUTED, rank: 3 };
    case 'active':        return { label: 'Active', color: 'var(--success)', soft: 'var(--success-soft)', rank: 4 };
    case 'expired':       return { label: 'Expired', ...DANGER, rank: 5 };
    case 'terminated':    return { label: 'Terminated', ...MUTED, rank: 6 };
    case 'superseded':    return { label: 'Superseded', ...MUTED, rank: 7 };
    default:              return { label: a.derivedState ?? '—', ...MUTED, rank: 8 };
  }
}
const isClosed = (a) => ['expired', 'terminated', 'superseded'].includes(a.derivedState);

/** A finding about the RECORD, not the agreement: what we have not written down. */
const DATE_GAPS = {
  'no-end-date': 'No end date — can never flag its own renewal',
  'no-start-date': 'No start date',
  'end-before-start': 'Ends before it starts',
  'zero-length': 'Starts and ends the same day',
};

function fmtSize(bytes) {
  if (!bytes) return '—';
  return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function TradingTermAgreements() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [form, setForm] = useState(null);      // the upload/edit form, or null
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [showClosed, setShowClosed] = useState(false);

  const { data: rows = [], isLoading, error: loadError } = useQuery({
    queryKey: ['trading-term-agreements'],
    queryFn: async () => {
      const r = await apiFetch('/trading-term-agreements');
      if (!r.ok) throw new Error(`Failed to load agreements: HTTP ${r.status}`);
      return r.json();
    },
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['master-customers-all'],
    queryFn: async () => {
      const r = await apiFetch('/master-customers?all=true');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: termsSets = [] } = useQuery({
    queryKey: ['customer-terms'],
    queryFn: async () => {
      const r = await apiFetch('/customer-terms');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 60 * 1000,
  });

  const upload = useMutation({
    mutationFn: async (body) => {
      const isEdit = !!body.agreementId;
      const r = await apiFetch(
        isEdit ? `/trading-term-agreements/${body.agreementId}` : '/trading-term-agreements',
        { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { setErr(''); setForm(null); qc.invalidateQueries({ queryKey: ['trading-term-agreements'] }); },
    onError: (e) => setErr(String(e?.message || e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => {
      const r = await apiFetch(`/trading-term-agreements/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['trading-term-agreements'] }); },
    onError: (e) => setErr(String(e?.message || e)),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(a => {
        if (!showClosed && isClosed(a)) return false;
        if (!q) return true;
        return `${a.customerName ?? ''} ${a.customerCode} ${a.title} ${a.fileName ?? ''} ${a.ownerName ?? ''} ${typeLabel(a.contractType)}`
          .toLowerCase().includes(q);
      })
      .sort((x, y) => stateBadge(x).rank - stateBadge(y).rank
        || String(x.customerName ?? '').localeCompare(String(y.customerName ?? '')));
  }, [rows, search, showClosed]);

  const counts = useMemo(() => ({
    closed: rows.filter(isClosed).length,
    notice: rows.filter(a => a.derivedState === 'notice-window').length,
    autoRenew: rows.filter(a => a.autoRenewRisk).length,
    expiring: rows.filter(a => a.derivedState === 'expiring').length,
    noEnd: rows.filter(a => a.dateIntegrity === 'no-end-date').length,
  }), [rows]);

  /*
   * Read the file in the browser and send it as base64 JSON.
   *
   * Not multipart: every other write in this app is JSON through apiFetch, which is what
   * attaches the bearer token, and a second transport would need its own auth path for no
   * gain at this size. Same shape as the CRM activity attachments (057).
   */
  async function pickFile(file) {
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1048576) {
      setErr(`"${file.name}" is ${(file.size / 1048576).toFixed(1)} MB — the limit is ${MAX_FILE_MB} MB.`);
      return;
    }
    const buf = await file.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    // Chunked: String.fromCharCode(...bytes) blows the argument limit on a multi-MB PDF.
    for (let i = 0; i < bytes.length; i += 0x8000) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    setErr('');
    setForm(f => ({
      ...(f ?? blankForm()),
      fileName: file.name,
      mime: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      contentBase64: btoa(binary),
      // A signed agreement is nearly always titled after the file it arrived as.
      title: f?.title || file.name.replace(/\.[^.]+$/, ''),
    }));
  }

  function blankForm() {
    return { agreementId: null, customerCode: '', title: '', startDate: '', endDate: '', supplyRoute: '', termsId: '', notes: '',
      status: 'active', contractType: 'trading-terms', noticeDays: '', autoRenew: false, nextReviewDate: '', ownerName: '' };
  }

  const routesFor = (code) => termsSets.filter(t => t.customerCode === code);

  return (
    <div>
      {loadError && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          {String(loadError.message || loadError)}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>Customer Contracts</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
            {rows.length} on the register
            {counts.notice > 0 && <span style={{ color: 'var(--danger)' }}> · {counts.notice} in notice window</span>}
            {counts.autoRenew > 0 && <span style={{ color: 'var(--danger)' }}> ({counts.autoRenew} auto-renew)</span>}
            {counts.expiring > 0 && <span style={{ color: 'var(--warn-text)' }}> · {counts.expiring} ending within 90 days</span>}
            {counts.noEnd > 0 && <span style={{ color: 'var(--warn-text)' }}> · {counts.noEnd} with no end date</span>}
            {counts.closed > 0 && ` · ${counts.closed} closed`}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
            Customer contracts only — supplier and service agreements (e.g. merchandisers) go in the
            purchasing register at purchasing.majans.com/contracts.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Customer, title or owner…"
            style={{ width: 180, fontSize: 12 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-body)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} style={{ accentColor: 'var(--info)' }} />
            Show expired / closed
          </label>
          <Button variant="primary" size="sm" onClick={() => { setErr(''); setForm(blankForm()); }}>+ Add contract</Button>
        </div>
      </div>

      {err && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{err}</div>
      )}

      {form && (
        <div style={{ ...CARD, background: 'var(--info-soft)', border: '1px solid var(--info-border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--info)', marginBottom: 10 }}>
            {form.agreementId ? 'Edit contract details' : 'New customer contract'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div>
              <div style={LBL}>Customer</div>
              <Select value={form.customerCode} onChange={e => setForm(f => ({ ...f, customerCode: e.target.value, termsId: '' }))}
                style={{ fontSize: 12, width: '100%' }}>
                <option value="">Choose…</option>
                {[...customers].sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
                  .map(c => <option key={c.customerCode} value={c.customerCode}>{c.name} ({c.customerCode})</option>)}
              </Select>
            </div>
            <div>
              <div style={LBL}>Title</div>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="FY27 Trading Terms" style={{ fontSize: 12, width: '100%' }} />
            </div>
            <div>
              <div style={LBL}>Direct or indirect</div>
              <Select value={form.supplyRoute} onChange={e => setForm(f => ({ ...f, supplyRoute: e.target.value }))} style={{ fontSize: 12, width: '100%' }}>
                <option value="">Covers both / not specified</option>
                <option value="DIRECT">Direct</option>
                <option value="VIA">Indirect</option>
              </Select>
            </div>
            <div>
              <div style={LBL}>Terms it covers (optional)</div>
              <Select value={form.termsId ?? ''} onChange={e => setForm(f => ({ ...f, termsId: e.target.value }))}
                style={{ fontSize: 12, width: '100%' }} disabled={!form.customerCode}>
                <option value="">—</option>
                {routesFor(form.customerCode).map(t => <option key={t.termsId} value={t.termsId}>{t.routeLabel}</option>)}
              </Select>
            </div>
            <div>
              <div style={LBL}>Start date</div>
              <Input type="date" value={form.startDate || ''} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={{ fontSize: 12, width: '100%' }} />
            </div>
            <div>
              <div style={LBL}>Finish date</div>
              <Input type="date" value={form.endDate || ''} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} style={{ fontSize: 12, width: '100%' }} />
            </div>
            <div>
              <div style={LBL}>Contract type</div>
              <Select value={form.contractType} onChange={e => setForm(f => ({ ...f, contractType: e.target.value }))} style={{ fontSize: 12, width: '100%' }}>
                {CONTRACT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </Select>
            </div>
            <div>
              <div style={LBL}>Status</div>
              <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={{ fontSize: 12, width: '100%' }}>
                <option value="draft">Draft (not yet signed)</option>
                <option value="active">Active (signed)</option>
                <option value="terminated">Terminated</option>
                <option value="superseded">Superseded</option>
              </Select>
            </div>
            <div>
              <div style={LBL}>Notice period (days)</div>
              <Input type="number" min={0} value={form.noticeDays ?? ''} onChange={e => setForm(f => ({ ...f, noticeDays: e.target.value }))}
                placeholder="e.g. 90" style={{ fontSize: 12, width: '100%' }} />
            </div>
            <div>
              <div style={LBL}>Next review</div>
              <Input type="date" value={form.nextReviewDate || ''} onChange={e => setForm(f => ({ ...f, nextReviewDate: e.target.value }))} style={{ fontSize: 12, width: '100%' }} />
            </div>
            <div>
              <div style={LBL}>Owner</div>
              <Input value={form.ownerName ?? ''} onChange={e => setForm(f => ({ ...f, ownerName: e.target.value }))}
                placeholder="Who decides the renewal" style={{ fontSize: 12, width: '100%' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-body)', cursor: 'pointer', paddingBottom: 6 }}>
                <input type="checkbox" checked={!!form.autoRenew} onChange={e => setForm(f => ({ ...f, autoRenew: e.target.checked }))}
                  style={{ accentColor: 'var(--info)' }} />
                Renews automatically unless notice is given
              </label>
            </div>
          </div>

          {!form.agreementId && (
            <div style={{ marginBottom: 10 }}>
              <div style={LBL}>Document</div>
              <input ref={fileRef} type="file" style={{ display: 'none' }}
                onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ''; }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Button size="sm" onClick={() => fileRef.current?.click()}>Choose file…</Button>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {form.fileName ? `${form.fileName} · ${fmtSize(form.sizeBytes)}` : `PDF, Word or an image — up to ${MAX_FILE_MB} MB`}
                </span>
              </div>
            </div>
          )}
          {form.agreementId && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
              Editing the details only. To replace the document itself, upload a new agreement —
              a signed record should never quietly change the file behind it.
            </div>
          )}

          <div>
            <div style={LBL}>Notes</div>
            <Textarea value={form.notes ?? ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
              style={{ fontSize: 12, width: '100%' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button variant="primary" size="sm" disabled={upload.isPending || !form.customerCode || !form.title.trim()}
              onClick={() => upload.mutate({ ...form, termsId: form.termsId || null, noticeDays: form.noticeDays === '' ? null : form.noticeDays })}>
              {upload.isPending ? 'Saving…' : form.agreementId ? 'Save details' : 'Add contract'}
            </Button>
            <Button size="sm" onClick={() => { setErr(''); setForm(null); }} disabled={upload.isPending}>Cancel</Button>
          </div>
        </div>
      )}

      {isLoading && <div style={{ color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>Loading agreements…</div>}

      {!isLoading && rows.length === 0 && !form && (
        <div style={{ ...CARD, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 30 }}>
          No customer contracts yet. Add the signed contract for an account with its finish date and
          notice period — it flags itself when the notice window opens and 90 days before it ends.
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--border)' }}>
                {['Customer', 'Contract', 'Route', 'Start', 'Finish', 'Notice by', 'Status', 'Owner', 'File', ''].map(h => <th key={h} style={TH}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td style={{ ...TD, color: 'var(--text-muted)' }} colSpan={10}>
                  Nothing matches{!showClosed && counts.closed > 0 ? ` — ${counts.closed} expired or closed ${counts.closed === 1 ? 'contract is' : 'contracts are'} hidden` : ''}.
                </td></tr>
              )}
              {filtered.map(a => {
                const st = stateBadge(a);
                return (
                  <tr key={a.agreementId}>
                    <td style={TD}>
                      <div>{a.customerName || a.customerCode}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.customerCode}</div>
                    </td>
                    <td style={TD}>
                      {a.title}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {typeLabel(a.contractType)}{a.autoRenew ? ' · auto-renews' : ''}
                      </div>
                      {a.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.notes}</div>}
                    </td>
                    <td style={{ ...TD, color: 'var(--text-muted)' }}>
                      {a.routeLabel || (a.supplyRoute === 'VIA' ? 'Indirect' : a.supplyRoute === 'DIRECT' ? 'Direct' : '—')}
                    </td>
                    <td style={{ ...TD, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{a.startDate || '—'}</td>
                    <td style={{ ...TD, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{a.endDate || '—'}</td>
                    <td style={{ ...TD, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {a.noticeByDate || (a.endDate && a.noticeDays == null ? 'No notice set' : '—')}
                    </td>
                    <td style={TD}>
                      <span style={{ background: st.soft, color: st.color, borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {st.label}
                      </span>
                      {a.unwatchedExposure && (
                        <div style={{ fontSize: 10, color: 'var(--danger)', marginTop: 3 }}>
                          {a.unwatchedExposure === 'end-passed' ? 'Draft past its end date' : 'Draft past its notice date'} — nobody is watching
                        </div>
                      )}
                      {a.dateIntegrity && (
                        <div style={{ fontSize: 10, color: 'var(--warn-text)', marginTop: 3 }}>{DATE_GAPS[a.dateIntegrity]}</div>
                      )}
                    </td>
                    <td style={{ ...TD, color: 'var(--text-muted)' }}>{a.ownerName || '—'}</td>
                    <td style={TD}>
                      {a.fileName
                        ? (
                          <DownloadLink id={a.agreementId} name={a.fileName} size={a.sizeBytes} apiFetch={apiFetch} onError={setErr} />
                        )
                        : <span style={{ color: 'var(--text-muted)' }}>No file</span>}
                    </td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                      <Button size="sm" onClick={() => {
                        setErr('');
                        setForm({
                          ...a, termsId: a.termsId ?? '', supplyRoute: a.supplyRoute ?? '', startDate: a.startDate ?? '', endDate: a.endDate ?? '',
                          notes: a.notes ?? '', noticeDays: a.noticeDays ?? '', nextReviewDate: a.nextReviewDate ?? '', ownerName: a.ownerName ?? '',
                        });
                      }}>Edit</Button>
                      <Button size="sm" variant="ghost" style={{ color: 'var(--danger)', marginLeft: 4 }}
                        onClick={() => {
                          if (!window.confirm(`Delete "${a.title}" for ${a.customerName || a.customerCode}?\n\nThe document is deleted with it and can't be recovered from the app.`)) return;
                          remove.mutate(a.agreementId);
                        }}>Delete</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * The file endpoint is bearer-authed, so a plain <a href> would 401 — the browser does
 * not attach our token. Fetch through apiFetch, then hand the blob to a throwaway
 * object URL.
 */
function DownloadLink({ id, name, size, apiFetch, onError }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      onClick={async () => {
        if (busy) return;
        setBusy(true);
        try {
          const r = await apiFetch(`/trading-term-agreement/${id}/file`);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = name;
          document.body.appendChild(a); a.click(); a.remove();
          // Revoke on the next tick — revoking synchronously races the download in Safari.
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
          onError(`Couldn't download "${name}": ${String(e?.message || e)}`);
        } finally {
          setBusy(false);
        }
      }}
      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--info)', fontSize: 12,
        cursor: busy ? 'wait' : 'pointer', textAlign: 'left', textDecoration: 'underline' }}
    >
      {busy ? 'Downloading…' : name}
      <span style={{ color: 'var(--text-muted)', textDecoration: 'none', marginLeft: 6 }}>{fmtSize(size)}</span>
    </button>
  );
}
